import { useCallback } from "react";
import type { AsyncThunkAction } from "@reduxjs/toolkit";
import { useAppDispatch } from "@redux/hooks";
import type { ApiThunkConfig } from "@shared-kernel/application/create-api-thunk";
import { apiErrorMessage } from "@shared-kernel/domain/errors/api-error";
import { useToast } from "@shared-kernel/primary/shared/shadcn/ui/use-toast";

// Issue d'une action : jamais rejetée, l'écran qui n'en fait rien n'a rien à rattraper. error est le body d'erreur de
// l'API (à passer à applyApiFieldErrors), ou une erreur sans body (session, réseau)
export type ApiActionOutcome<R> = { ok: true; value: R } | { ok: false; error: unknown };

interface Feedback<R> {
  // Toast du succès ; absent quand l'écran le signale autrement
  success?: string | ((value: R) => string);
  // Toast du refus quand l'API ne dit pas pourquoi
  failure: string;
}

// Toast d'un refus : le detail de l'API, sinon le message de l'écran
export const useRefusalToast = () => {
  const { toast } = useToast();
  return useCallback(
    (error: unknown, fallback: string) => toast({ description: apiErrorMessage(error, fallback), variant: "destructive" }),
    [toast],
  );
};

// Lance une action de l'API et signale son issue par un toast. Sans feedback, aucun toast : l'écran signale l'issue
// lui-même (upload dont le refus s'affiche sous le champ)
export const useApiAction = () => {
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const toastRefusal = useRefusalToast();

  return useCallback(
    <R>(action: AsyncThunkAction<R, unknown, ApiThunkConfig>, feedback?: Feedback<R>): Promise<ApiActionOutcome<R>> =>
      dispatch(action)
        .unwrap()
        .then(
          (value): ApiActionOutcome<R> => {
            const success = feedback?.success;
            if (success) toast({ description: typeof success === "function" ? success(value) : success });
            return { ok: true, value };
          },
          (error: unknown): ApiActionOutcome<R> => {
            if (feedback) toastRefusal(error, feedback.failure);
            return { ok: false, error };
          },
        ),
    [dispatch, toast, toastRefusal],
  );
};
